import { useProductReportControllerGetProductCardEconomy } from "@/api/types/main";
import {
  GetProductCardEconomyResponseDTO,
  NullableNumberCeilDTO,
  NumberCeilDTO,
} from "@/api/types/main/schemas";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import { TanstackTable } from "@/components/custom-ui/tables/tanstack.table";
import {
  MemoizedTanstackTableBody,
  TanstackTableBody,
} from "@/components/custom-ui/tables/tanstack.table.body";
import {
  MemoizedTanstackTableHeader,
  TanstackTableHeader,
} from "@/components/custom-ui/tables/tanstack.table.header";
import { DndSortableHeadersContext } from "@/components/tables/dnd.sortable.headers.context";
import { useCommersCardColumns } from "@/hooks/useCommersCardColumns";
import UseGetFlatRowsTable from "@/hooks/useGetFlatRowsTable";
import { useTableDataRangeStore } from "@/store/table.data.range";
import { TableIds } from "@/store/tables";
import { format } from "date-fns";
import { useParams } from "react-router-dom";

export type TransformCardData = {
  label: string;
  data: NumberCeilDTO[] | NullableNumberCeilDTO[];
};

const READABLE_ECONOMIC_CARD_LABELS: Record<keyof GetProductCardEconomyResponseDTO, string> = {
  markup: "Наценка",
  netProfit: "Чистая прибыль",
  netProfitPercent: "Чистая прибыль, %",
  revenue: "Выручка",
  revenuePercent: "Выручка, %",
};

export const CommersCardEconomics = () => {
  const dateRange = useTableDataRangeStore((store) => store.dateRange);
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const { data, isLoading, isError, error } = useProductReportControllerGetProductCardEconomy(
    productId,
    {
      periodEnd: format(dateRange.to, "yyyy-MM-dd"),
      periodStart: format(dateRange.from, "yyyy-MM-dd"),
    },
    {
      query: {
        queryKey: ["card", "economics", "history", dateRange.from, dateRange.to, productId],
        select: (data) => {
          const keys = Object.keys(data) as (keyof GetProductCardEconomyResponseDTO)[];

          return keys.map(
            (field): TransformCardData => ({
              label: READABLE_ECONOMIC_CARD_LABELS[field],
              data: data[field] || "Значение не добавлено",
            })
          );
        },
      },
    }
  );
  const columns = useCommersCardColumns({ dateRange: dateRange });

  const { table, columnSizeVars, columnOrder } = UseGetFlatRowsTable({
    data: data || [],
    columns,
    tableId: TableIds.Commers_EconomicCard,
  });
  if (isError) return <div>{error?.message}</div>;
  if (isLoading) return <LoadingSpinner />;
  return (
    <div className="h-full flex flex-col">
      <TanstackTable style={{ width: table.getTotalSize(), ...columnSizeVars }} className="flex-1">
        {table.getState().columnSizingInfo.isResizingColumn ? (
          <>
            <MemoizedTanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
            <MemoizedTanstackTableBody table={table} columnOrder={columnOrder} />
          </>
        ) : (
          <>
            <DndSortableHeadersContext table={table} tableId={TableIds.Commers_EconomicCard}>
              <TanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
              <TanstackTableBody table={table} columnOrder={columnOrder} />
            </DndSortableHeadersContext>
          </>
        )}
      </TanstackTable>
    </div>
  );
};
